/**
 * QueryString Context
 */

import { Dictionary, omit } from 'lodash';
import * as qs from 'query-string';
import * as React from 'react';

import { TaxonomyId, TaxonomyType } from '../../relay/selectors/taxonomy';
import { BudgetFilter } from './budgetFilter';
import { replaceQueryString, updateQueryString } from './helpers';
import { LicenseFilter } from './license';
import {
  getSearchAsArray,
  parseTaxonomyQuery,
  SearchFilter,
  searchQueryRegEx,
  searchQueryTemplate,
} from './searchFilters';
import { SortOrderSerializer } from './sortOrder';
import { TempoFilter } from './tempoFilter';
import { ContextAction, IQueryStringContext, QueryStringParam, SimpleContextAction } from './types';

export {
  IQueryStringContext,
  QueryStringParam,
};

export const removeQsParamFromContext: ContextAction<QueryStringParam> = (keyToRemove, ctx, { match, router }) => {
  const currentQuery = qs.parse(match.location.search) as Dictionary<string>;

  replaceQueryString(omit(currentQuery, keyToRemove), match.location, router);

  switch (keyToRemove) {
    case QueryStringParam.Search:
      return {
        ...ctx,
        filters: {
          ...ctx.filters,
          not_term_filters: [],
          term_filters: [],
        },
        query: '',
      };
    case QueryStringParam.Budget:
      return {
        ...ctx,
        filters: {
          ...ctx.filters,
          budget: undefined,
        },
      };
    case QueryStringParam.Similarity:
      const { similarity, ...rest } = ctx.filters;
      return {
        ...ctx,
        filters: rest,
      };
    case QueryStringParam.Tempo:
      return {
        ...ctx,
        filters: {
          ...ctx.filters,
          tempo: undefined,
        },
      };
    case QueryStringParam.License:
      return {
        ...ctx,
        license_params: [],
      };
    default:
      return ctx;
  }
};

export const addBudgetFilterToContext: ContextAction<GQL.ITrackSearchBudgetFilter> =
  (budget, ctx, { match: { location }, router }) => {
    updateQueryString({
      [QueryStringParam.Budget]: BudgetFilter.serialize(budget),
    }, location, router);

    return {
      ...ctx,
      filters: {
        ...ctx.filters,
        budget,
      },
    };
  };

export const addSimilarityFilterToContext: ContextAction<string> =
  (trackId, ctx, { match: { location }, router }) => {
    updateQueryString({
      [QueryStringParam.Similarity]: trackId,
    }, location, router);

    return {
      ...ctx,
      filters: {
        ...ctx.filters,
        similarity: {
          track_id: trackId,
          feature: GQL.TrackSimilarityFeature.Default,
        },
      },
    };
  };

export const addTempoFilterToContext: ContextAction<GQL.IIntegerRangeFilter | undefined> =
  (tempo, ctx, { match: { location }, router }) => {
    updateQueryString({
      [QueryStringParam.Tempo]: TempoFilter.serialize(tempo),
    }, location, router);

    return {
      ...ctx,
      filters: {
        ...ctx.filters,
        tempo,
      },
    };
  };

export const setSearchSortOrderInContext: ContextAction<GQL.ISearchSortOrder> =
  (sortOrder, ctx, { match: { location }, router }) => {
    updateQueryString({
      [QueryStringParam.SortOrder]: SortOrderSerializer.serialize(sortOrder),
    }, location, router);

    return {
      ...ctx,
      sort: sortOrder,
    };
  };

export const updateSearchFiltersInContext: ContextAction<string[]> =
  (search, ctx, { match, router }) => {
  const queryString = {
    // Do not serialize here since it'll need to be re-serialize on every query string update
    [QueryStringParam.Search]: `${search}`,
  };

  if (search.length > 0) {
    updateQueryString(queryString, match.location, router);
  } else {
    removeQsParamFromContext(QueryStringParam.Search, ctx, { match, router });
  }

  const {
    filters: {
      not_term_filters,
      term_filters,
    },
  } = SearchFilter.unserialize(queryString);

  return {
    ...ctx,
    filters: {
      ...ctx.filters,
      not_term_filters,
      term_filters,
    },
  };
};

export const addSearchFilterToContext: ContextAction<[SearchFilter, boolean]> =
  ([term, exclude], ctx, withRouter) => {
    const query = withRouter.match.location.query;
    const taxonomyId = term.taxonomy ? term.taxonomy.id : undefined;
    const termQuery = taxonomyId === TaxonomyId[TaxonomyType.Lyrics] ?
      SearchFilter.serializeTaxonomyQuery(term) :
      SearchFilter.serializeTerm(term, exclude);
    const termRegExp = new RegExp(`(!?${term.name})`, 'g');
    let search: string[] = [];

    if (QueryStringParam.Search in query) {
      search = getSearchAsArray(query);

      const oppositeTermMatch = query[QueryStringParam.Search].match(termRegExp);

      if (oppositeTermMatch) {
        const [termMatch] = oppositeTermMatch;
        const termIndex = search.indexOf(termMatch);
        search.splice(termIndex, 1, termQuery);
      } else {
        search = [...search, termQuery];
      }
    } else {
      search = [termQuery];
    }

    return updateSearchFiltersInContext(search, ctx, withRouter);
  };

export const removeSearchFilterFromContext: ContextAction<[SearchFilter, boolean]> =
  ([term, exclude], ctx, withRouter) => {
    const query = withRouter.match.location.query;
    const taxonomyId = term.taxonomy ? term.taxonomy.id : undefined;
    const termQuery = taxonomyId === TaxonomyId[TaxonomyType.Lyrics] ?
      SearchFilter.serializeTaxonomyQuery(term) :
      SearchFilter.serializeTerm(term, exclude);
    let search: string[] = [];

    if (QueryStringParam.Search in query) {
      search = getSearchAsArray(query);
      const termIndex = search.indexOf(termQuery);
      search.splice(termIndex, 1);
    }

    return updateSearchFiltersInContext(search, ctx, withRouter);
  };

export const addTaxonomyQueryToContext: ContextAction<SearchFilter> =
  (term, ctx, withRouter) => {
    const query = withRouter.match.location.query;
    const termQuery = SearchFilter.serializeTaxonomyQuery(term);
    let search: string[] = [];

    if (QueryStringParam.Search in query) {
      search = getSearchAsArray(query);

      const existingQueryMatch = parseTaxonomyQuery(query[QueryStringParam.Search]);

      if (existingQueryMatch) {
        const termIndex = search.findIndex((value) => {
          return value.startsWith(existingQueryMatch.key);
        });
        search.splice(termIndex, 1, termQuery);
      } else {
        search = [...search, termQuery];
      }
    } else {
      search = [termQuery];
    }

    return {
      ...updateSearchFiltersInContext(search, ctx, withRouter),
      taxonomy_query: {
        query: term.name,
        taxonomy_id: term.taxonomy!.id,
      },
    };
  };

export const removeTaxonomyQueryFromContext: ContextAction<SearchFilter> =
  (term, ctx, withRouter) => {
    return {
      ...removeSearchFilterFromContext([term, false], ctx, withRouter),
      taxonomy_query: undefined as any,
    };
  };

export const clearSearchQueryFromContext: SimpleContextAction = (ctx, { match, router }) => {
  const query = match.location.query;
  let search: string[] = [];

  if (QueryStringParam.Search in query) {
    search = getSearchAsArray(query);

    const searchQueryMatch = query[QueryStringParam.Search].match(searchQueryRegEx);

    if (searchQueryMatch) {
      const [firstMatch] = searchQueryMatch;
      const queryIndex = search.indexOf(firstMatch);
      search.splice(queryIndex, 1);
    }
  }

  if (search.length > 0) {
    updateQueryString({
      [QueryStringParam.Search]: `${search}`,
    }, match.location, router);
  } else {
    removeQsParamFromContext(QueryStringParam.Search, ctx, { match, router });
  }

  return { ...ctx, query: '' };
};

export const saveLicenseToContext: ContextAction<GQL.ILicenseUsageParamsInput[]> =
  (licenseParams, ctx, { match, router }) => {
    updateQueryString({
      [QueryStringParam.License]: LicenseFilter.serialize(licenseParams),
    }, match.location, router);

    return {
      ...ctx,
      license_params: licenseParams,
    };
  };

export const setSearchQueryInContext: ContextAction<string> = (searchQuery, ctx, { match, router }) => {
  const query = match.location.query;
  const searchQueryString = searchQueryTemplate({ searchQuery });

  let search: string[] = [];

  if (QueryStringParam.Search in query) {
    search = getSearchAsArray(query);

    const searchQueryMatch = query[QueryStringParam.Search].match(searchQueryRegEx);

    if (searchQueryMatch) {
      const [firstMatch] = searchQueryMatch;
      const queryIndex = search.indexOf(firstMatch);
      search.splice(queryIndex, 1, searchQueryString);
    } else {
      search = [
        searchQueryString,
        ...getSearchAsArray(query),
      ];
    }
  } else {
    search = [searchQueryString];
  }

  updateQueryString({
    [QueryStringParam.Search]: `${search}`,
  }, match.location, router);

  return {
    ...ctx,
    query: searchQuery,
  };
};

export const clearFiltersFromContext: SimpleContextAction = (ctx, { match, router }) => {
  replaceQueryString(
    omit(match.location.query,
      QueryStringParam.Budget,
      QueryStringParam.Search,
      QueryStringParam.Tempo,
      QueryStringParam.SortOrder,
    ),
    match.location, router,
  );

  return {
    ...ctx,
    query: '',
    filters: {
      budget: undefined,
      not_term_filters: [],
      tempo: undefined,
      term_filters: [],
    },
  };
};

export const QueryStringContext = React.createContext<IQueryStringContext>({
  addBudgetFilter: () => null,
  addSearchFilter: () => null,
  addTempoFilter: () => null,
  addTaxonomyQuery: () => null,
  addSimilarityFilter: () => null,
  clearFilters: () => null,
  clearSearchQuery: () => null,
  removeFilter: () => null,
  removeTaxonomyQuery: () => null,
  removeBudgetFilter: () => null,
  removeLicense: () => null,
  removeSearchFilter: () => null,
  removeSimilarityFilter: () => null,
  removeTempoFilter: () => null,
  saveLicense: () => null,
  setSearchQuery: () => null,
  setSearchSortOrder: () => null,
});



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/contexts/queryString/index.ts